import React, { Ref, useCallback, useContext, useMemo, useState } from 'react';
import { areEqual, ListChildComponentProps } from 'react-window';
import { isUnrenderable, joinClassNames } from '@tanium/coreui-utils';
import { Box, BoxProps } from '@tanium/react-box';
import { TooltippedCheckbox } from '@tanium/react-checkbox';
import { Disclosure } from '@tanium/react-disclosure';
import styled, { css } from '@tanium/react-emotion-9';
import { Shimmer } from '@tanium/react-shimmer';
import { useTheme } from '@tanium/react-theme-context';
import { Border1 } from '@tanium/react-theme-definition';
import { ThemeSpace } from '@tanium/theme-data';
import { RibbonListContext } from '../../context/RibbonListContext';
import { RibbonListExpansionContext } from '../../context/RibbonListExpansionContext';
import { RibbonListSelectionContext } from '../../context/RibbonListSelectionContext';
import {
  DisclosureBox,
  RowEmphasisFill,
  RowExpandedFill,
  RowFill,
  RowHoverFill,
  RowSelectedFill,
  SubtitleText,
} from '../../themeDefinitions';
import { Alignment, alignmentToFlexAlignValue } from '../../types/Alignment';
import { PrimaryColumn, RibbonColumn } from '../../types/StackedRibbonListComponentTypes';
import { Actions } from './Actions';

interface ItemRendererProps {
  index: ListChildComponentProps['index'];
  style?: ListChildComponentProps['style'];
}

interface ItemRendererInternalProps extends ItemRendererProps {
  forwardedRef: Ref<HTMLDivElement>;
}

interface FieldsProps<TItem> {
  index: number;
  isLoaded: boolean;
  itemData: TItem;
  columns: RibbonColumn<TItem>[];
}

const PointerBox = styled(Box, { shouldForwardProp: (p) => p !== 'disabled' })<
  BoxProps<'div'> & { disabled?: boolean }
>(({ disabled }) => ({
  cursor: disabled ? 'not-allowed' : 'pointer',
}));

/**
 * Hook for retrieving classnames dependent on the theme.
 */
const useMemoizedClassnames = (enableRowHighlight?: boolean) => {
  const theme = useTheme();
  return useMemo(() => {
    const baseRibbonStyle = css([
      {
        padding: 8,
        paddingLeft: 0,
        paddingRight: 0,
        fontSize: 14,
        lineHeight: 1,
        background: RowFill(theme),
        borderBottom: theme.borders[1],
        borderBottomColor: Border1(theme),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
      enableRowHighlight !== false && {
        ':hover': {
          background: RowHoverFill(theme),
        },
      },
    ]);

    const emphasizedStyle = css(baseRibbonStyle, {
      background: RowEmphasisFill(theme),
    });

    const selectedStyle = css(baseRibbonStyle, {
      background: RowSelectedFill(theme),
    });

    const expandableRowStyle = css({
      background: RowExpandedFill(theme),
      margin: 16,
      marginBottom: 8,
      padding: 16,
    });

    return {
      baseRibbonStyle,
      emphasizedStyle,
      selectedStyle,
      expandableRowStyle,
    };
  }, [enableRowHighlight, theme]);
};

const useMemoizedSubtitleClassName = () => {
  const theme = useTheme();
  return useMemo(
    () =>
      css({
        marginTop: 8,
        fontSize: 12,
        color: SubtitleText(theme),
      }),
    [theme],
  );
};

const topRowStyle = css({
  display: 'flex',
  flexDirection: 'row',
});

const expandableSelectStyle = css({
  marginLeft: 32,
});

const noWrapStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const fieldStyle = ({
  alignment,
  width,
  grow = 1,
}: {
  alignment: Alignment | undefined;
  width?: number;
  grow?: number;
}) =>
  css(
    {
      paddingLeft: 8,
      paddingRight: 8,
      flexBasis: 0,
      flexGrow: grow,
      minWidth: 120,
      // Allows long, unbroken strings to be broken within the word
      wordBreak: 'break-word',
    },
    alignment && { textAlign: alignment },
    width && {
      minWidth: width,
      maxWidth: width,
      flexBasis: width,
      flexGrow: 0,
    },
  );

const titleStyle = css({
  fontWeight: 'bold',
});

const Fields = React.memo(
  <TItem extends unknown>({ columns, index, itemData, isLoaded }: FieldsProps<TItem>) => {
    const subtitleClassName = useMemoizedSubtitleClassName();

    return (
      <>
        {columns.map((c: RibbonColumn<TItem>) => {
          const { alignment, center, dataKey, noWrap, primary, width, grow } = c;
          const { subtitleKey, subtitleRenderer } = c as PrimaryColumn<TItem>;
          const renderField =
            c.renderer || (() => (dataKey ? (itemData as Record<string, string>)[dataKey] : null));
          const renderSubtitle =
            subtitleRenderer ||
            (() => subtitleKey && (itemData as Record<string, string>)[subtitleKey]);
          const renderedSubtitle =
            primary && isLoaded && (subtitleRenderer || subtitleKey)
              ? renderSubtitle({ itemData, index, dataKey: subtitleKey })
              : null;
          return (
            <div
              role="cell"
              key={dataKey}
              className={fieldStyle({
                alignment: alignment ?? (center ? 'center' : undefined),
                width,
                grow,
              })}
            >
              <div className={joinClassNames(primary && titleStyle, noWrap && noWrapStyle)}>
                {isLoaded ? renderField({ itemData, index, dataKey }) : <Shimmer />}
              </div>
              {!isUnrenderable(renderedSubtitle) && (
                <div className={subtitleClassName}>{renderedSubtitle}</div>
              )}
            </div>
          );
        })}
      </>
    );
  },
);

const Renderer = React.memo(({ forwardedRef, index, style }: ItemRendererInternalProps) => {
  const {
    actions,
    columns,
    dataGetter,
    enableRowHighlight,
    isIdEmphasized,
    isItemLoaded,
    isLoading,
    isSelectionEnabled,
    itemIdGetter,
    selectionTooltipContent,
    verticalAlignment = 'middle',
    ...otherProps
  } = useContext(RibbonListContext);

  // More specific alignment values default to the `verticalAlignment` when undefined.
  const {
    actionsVerticalAlignment = verticalAlignment,
    checkboxVerticalAlignment = verticalAlignment,
    disclosureVerticalAlignment = verticalAlignment,
  } = otherProps;

  const fieldsAlignItems = useMemo(
    () => alignmentToFlexAlignValue(verticalAlignment),
    [verticalAlignment],
  );

  const actionsAlignItems = useMemo(
    () => alignmentToFlexAlignValue(actionsVerticalAlignment),
    [actionsVerticalAlignment],
  );

  const checkboxAlignItems = useMemo(
    () => alignmentToFlexAlignValue(checkboxVerticalAlignment),
    [checkboxVerticalAlignment],
  );

  const disclosureAlignItems = useMemo(
    () => alignmentToFlexAlignValue(disclosureVerticalAlignment),
    [disclosureVerticalAlignment],
  );

  const {
    isExpandable,
    isIdExpanded,
    toggleExpansion,
    expandableContent,
    hideExpansionDisclosure,
  } = useContext(RibbonListExpansionContext);
  const { isSelectable, isIdSelected, toggleSelection } = useContext(RibbonListSelectionContext);

  const [isHovering, setIsHovering] = useState(false);
  const onMouseEnterRibbon = useCallback(() => {
    setIsHovering(true);
  }, []);
  const onMouseLeaveRibbon = useCallback(() => {
    setIsHovering(false);
  }, []);

  const classNames = useMemoizedClassnames(enableRowHighlight);

  const isLoaded = !isLoading && isItemLoaded(index);
  const itemData = isLoaded ? dataGetter(index) : undefined;
  const itemId = itemIdGetter(index);
  const isEmphasized = isIdEmphasized?.(itemId);
  const isExpanded = isExpandable && isIdExpanded?.(itemId);
  const isSelected = isSelectable && isIdSelected(itemId);

  const getClassName = () => {
    if (isEmphasized) {
      return classNames.emphasizedStyle;
    }
    if (isSelected) {
      return classNames.selectedStyle;
    }
    return classNames.baseRibbonStyle;
  };

  const handleToggleSelection = useCallback(
    () => toggleSelection(itemId),
    [itemId, toggleSelection],
  );

  const handleToggleExpansion = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      if (isLoaded) {
        e.stopPropagation();
        toggleExpansion?.(itemId);
      }
    },
    [isLoaded, itemId, toggleExpansion],
  );

  const isSelectionDisabled = !isLoaded || isSelectionEnabled === false;
  return (
    <div role="row" ref={forwardedRef} style={style} key={index}>
      <div
        role="presentation"
        onMouseEnter={onMouseEnterRibbon}
        onMouseLeave={onMouseLeaveRibbon}
        className={getClassName()}
      >
        <div role="presentation" className={topRowStyle}>
          {isSelectable && (
            <Box role="cell" display="flex" alignItems={checkboxAlignItems} mx={1}>
              <TooltippedCheckbox
                checked={isSelected}
                data-checkbox-for={itemId}
                disabled={isSelectionDisabled}
                onChange={handleToggleSelection}
                tooltipContent={selectionTooltipContent}
                tooltipPlacement="right"
              />
            </Box>
          )}
          {isExpandable && !hideExpansionDisclosure && (
            <PointerBox
              role="cell"
              disabled={!isLoaded}
              px={1}
              py={ThemeSpace.minor[1]}
              color={DisclosureBox}
              display="flex"
              alignItems={disclosureAlignItems}
              data-expandable-for={itemId}
              onClick={handleToggleExpansion}
            >
              <Disclosure
                aria-expanded={isExpanded}
                isCollapsed={!isExpanded}
                role={isLoaded ? 'button' : undefined}
              />
            </PointerBox>
          )}

          <Box role="presentation" display="flex" alignItems={fieldsAlignItems} flexGrow={1}>
            <Fields columns={columns} index={index} itemData={itemData} isLoaded={isLoaded} />
          </Box>
          {actions && isLoaded && (
            <Box role="cell" display="flex" alignItems={actionsAlignItems}>
              <Actions
                actions={actions}
                index={index}
                itemData={itemData}
                isHovering={isHovering}
              />
            </Box>
          )}
        </div>
        {expandableContent && isExpanded && (
          <div
            role="cell"
            data-expandable-content={itemId}
            className={joinClassNames(
              classNames.expandableRowStyle,
              isSelectable && expandableSelectStyle,
            )}
          >
            {expandableContent.renderer({ itemData, index })}
          </div>
        )}
      </div>
    </div>
  );
}, areEqual);

export const ItemRenderer = React.forwardRef<HTMLDivElement, ItemRendererProps>((props, ref) => (
  <Renderer {...props} forwardedRef={ref} />
));
